import { PusherEvent } from '@mondaygpt/constants'
import { BrandLearningCompletedPayload } from '@mondaygpt/types'
import { useCallback } from 'react'
import { useListenToPusherEvent } from 'modules/core/hooks/useListenToPusherEvent'

/**
 * Listens for `PusherEvent.BrandLearningCompleted` on the authenticated user
 * channel and invokes `onCompleted` with the typed payload.
 *
 * Spec: `docs/superpowers/specs/2026-05-20-brand-learning-from-instagram-design.md`.
 *
 * The brand-learning refresh path on the server is fire-and-forget (the REST
 * call returns 202 before mining starts). The Pusher event is how the UI
 * learns mining is done so it can re-fetch the per-source summary without
 * polling. Used by BrandCardStep (onboarding kickoff progress) and the
 * BrandKitPage refresh button.
 *
 * The hook auto-subscribes via `useListenToPusherEvent` (which itself
 * stabilizes the callback via a ref), so consumers don't need to memoize the
 * callback. Cleanup happens automatically on unmount.
 *
 * Pass `expectedBusinessId` to filter events to a single business — necessary
 * for agency owners whose user channel fans out events across every business
 * they're a member of.
 */
export const useBrandLearningPusher = (
  onCompleted: (payload: BrandLearningCompletedPayload) => void,
  expectedBusinessId?: string
): void => {
  const filteredCallback = useCallback(
    (payload: BrandLearningCompletedPayload) => {
      if (
        expectedBusinessId &&
        String(payload.businessId).trim() !== String(expectedBusinessId).trim()
      ) {
        return
      }
      onCompleted(payload)
    },
    [onCompleted, expectedBusinessId]
  )
  useListenToPusherEvent<BrandLearningCompletedPayload>(
    PusherEvent.BrandLearningCompleted,
    filteredCallback
  )
}
